import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  List,
  Popover,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { type UIEvent, useCallback, useState } from 'react';

import { useMarkAllNotificationsRead, useNotificationList } from '@/hooks/useNotifications';
import { NotificationRow } from './NotificationRow';
import { useNotificationClick } from './useNotificationClick';

interface NotificationPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const SCROLL_THRESHOLD_PX = 80;

function PanelBody({ onClose }: { onClose: () => void }) {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNotificationList(unreadOnly);
  const { mutate: markAll } = useMarkAllNotificationsRead();
  const handleClick = useNotificationClick(onClose);

  const items = data?.pages.flatMap((page) => page.items) ?? [];

  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const el = event.currentTarget;
      const nearEnd = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX;
      if (nearEnd && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6" component="h2">
          Notifications
        </Typography>
        <Button size="small" onClick={() => markAll()}>
          Mark all as read
        </Button>
      </Box>
      <Box sx={{ px: 2, pb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={unreadOnly}
              onChange={(event) => setUnreadOnly(event.target.checked)}
            />
          }
          label="Unread only"
        />
      </Box>
      <Divider />
      <Box onScroll={handleScroll} sx={{ overflowY: 'auto', flex: 1, minHeight: 120 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography
            sx={{
              color: 'text.secondary',
              p: 3,
              textAlign: 'center',
            }}
          >
            No notifications yet.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} onClick={handleClick} />
            ))}
          </List>
        )}
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function NotificationPanel({ open, anchorEl, onClose }: NotificationPanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-label="Notifications">
        <PanelBody onClose={onClose} />
      </Dialog>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 400, maxWidth: '100vw' } } }}
    >
      <PanelBody onClose={onClose} />
    </Popover>
  );
}
